import { motion, AnimatePresence } from "framer-motion";
import { useContext, useMemo } from "react";
import { BotContext } from "@/pages/cbb-builder/store";

const COMPLETED_STATUSES = new Set([
  "Training Complete",
  "Re-sync Complete",
  "Link Rejected",
  "File Rejected",
  "Limit Reached",
  "Re-sync Failed",
  "Failed",
]);

const OnboardingProgressToast = () => {
  const [store] = useContext(BotContext);

  const progressData = useMemo(() => {
    const aiSource = store.botData?.aiSource ?? {};
    let total = 0;
    let completed = 0;

    // Check all source types
    const sourceTypes = [
      "urls",
      "documents",
      "googlepicker",
      "notion",
      "confluence",
      "salesforce",
    ];

    sourceTypes.forEach((key) => {
      const items = aiSource[key] ?? [];
      total += items.length;
      completed += items.filter((item: any) =>
        COMPLETED_STATUSES.has(item.status ?? "In Progress"),
      ).length;
    });

    return { total, completed };
  }, [store.botData?.aiSource]);

  const { total, completed } = progressData;
  const inProgress = total > 0 && completed < total;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <AnimatePresence>
      {inProgress && (
        <motion.div
          className="onboarding-progress-toast fixed bottom-[85px] right-[12px] flex items-center gap-[10px] rounded-[16px] bg-white p-[10px] shadow-[0_1px_7px_0_rgba(0,0,0,0.08)]"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          transition={{ type: "spring", bounce: 0.15, duration: 0.6 }}
        >
          <div className="relative flex h-[80px] w-[80px] shrink-0 items-center justify-center">
            <style>{`
              @keyframes onboarding-spinner {
                100% { transform: rotate(1turn); }
              }
              @keyframes onboarding-pulse-border {
                0%, 100% {
                  transform: scale(1);
                  opacity: 0.6;
                }
                50% {
                  transform: scale(1.02);
                  opacity: 1;
                }
              }
              @keyframes onboarding-pulse-spinner {
                0%, 100% {
                  opacity: 0.75;
                  filter: drop-shadow(0 0 1px rgba(24, 123, 211, 0.1));
                }
                50% {
                  opacity: 1;
                  filter: drop-shadow(0 0 6px rgba(24, 123, 211, 0.6));
                }
              }
            `}</style>
            {/* Thin outer ring */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                border: "1px solid #91C8F9",
                animation: "onboarding-pulse-border 2.5s ease-in-out infinite",
              }}
            />
            {/* Static track ring */}
            <div
              className="absolute inset-[2px] rounded-full"
              style={{
                border: "8px solid #F2F2F2",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                animation: "onboarding-pulse-spinner 2s ease-in-out infinite",
              }}
            >
              <div
                className="absolute inset-[3px] rounded-full"
                style={{
                  background: `radial-gradient(farthest-side, #187BD3 94%, transparent) top/8px 8px no-repeat, conic-gradient(transparent 30%, #187BD3)`,
                  WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - 8px), #000 0)`,
                  mask: `radial-gradient(farthest-side, transparent calc(100% - 8px), #000 0)`,
                  animation: "onboarding-spinner 1.5s infinite linear",
                }}
              />
            </div>
            <span className="text-[14px] font-bold leading-[110%] tracking-tight text-[#444]">
              {percentage}%
            </span>
          </div>

          <div className="flex flex-col gap-[8px]">
            <span className="text-[13px] font-bold leading-[110%] text-[#444]">
              Scanning sources...
            </span>
            <div className="flex w-max items-center rounded-full border border-[rgba(0,0,0,0.08)] bg-[#F9FAFB] px-[8px] py-[5px] text-[11px] font-light leading-[110%] text-[#555]">
              <span className="mr-[4px] text-[11px] font-normal text-[#444]">
                {completed} / {total}
              </span>{" "}
              items processed
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OnboardingProgressToast;
